// =============================================================================
// PDF Renderer Hook
// =============================================================================
// Loads a PDF document and renders pages to a canvas element.
// Adapted from SigningCeremonyPage pattern.
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdfjs worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

interface UsePdfRendererOptions {
  /** Function that returns a URL for the PDF document */
  getDocumentUrl: () => Promise<string>;
  /** Current page number to render (1-indexed) */
  currentPage: number;
  /** Whether to load the PDF (set false if not ready) */
  enabled?: boolean;
}

interface UsePdfRendererReturn {
  pdfDoc: pdfjsLib.PDFDocumentProxy | null;
  pdfLoading: boolean;
  pdfError: string | null;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  /** Actual rendered page height in pixels (for container sizing) */
  pageHeight: number;
  /** Actual rendered page width in pixels */
  pageWidth: number;
  /** Reload the PDF */
  reload: () => void;
}

export function usePdfRenderer({
  getDocumentUrl,
  currentPage,
  enabled = true,
}: UsePdfRendererOptions): UsePdfRendererReturn {
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageHeight, setPageHeight] = useState(792);
  const [pageWidth, setPageWidth] = useState(612);
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const containerRef = useRef<HTMLDivElement>(null!);

  // Load the PDF document
  const loadPdf = useCallback(async () => {
    if (!enabled) return;
    try {
      setPdfLoading(true);
      setPdfError(null);
      const url = await getDocumentUrl();
      const doc = await pdfjsLib.getDocument(url).promise;
      setPdfDoc(doc);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setPdfError(err instanceof Error ? err.message : 'Failed to load PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [getDocumentUrl, enabled]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  // Render the current page to the canvas
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    let cancelled = false;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const containerWidth = containerRef.current?.clientWidth || 612;
        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min(containerWidth / viewport.width, 2);
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current!;
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        setPageHeight(scaledViewport.height);
        setPageWidth(scaledViewport.width);

        const context = canvas.getContext('2d')!;
        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
        }).promise;
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to render page:', err);
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage]);

  // Re-render on window resize
  useEffect(() => {
    if (!pdfDoc) return;

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Trigger re-render by creating a new effect cycle
        setPdfDoc((prev) => prev);
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [pdfDoc]);

  return {
    pdfDoc,
    pdfLoading,
    pdfError,
    canvasRef,
    containerRef,
    pageHeight,
    pageWidth,
    reload: loadPdf,
  };
}
